// ============================================================
// exams-student.jsx — نسخه نهایی
// طراحی دقیقاً مطابق فایل HTML + داده‌های واقعی از Context‌ها
// ============================================================
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import moment from 'moment-jalaali';
import './exams-student.css';
import { useAuth } from '../../context/AuthContext.jsx';
import { useCoins } from '../../context/CoinContext.jsx';
import { useExams, toNumber, calculatePassThreshold, idsEqual } from './ExamContext.jsx';
import { normalizeFieldId, normalizeGradeId, getGrade } from '../../core/education/curriculum.js';

// ═══════════════════════════════════════════════════════════════
//  HELPERS
// ═══════════════════════════════════════════════════════════════
const FA = '۰۱۲۳۴۵۶۷۸۹';
const toFa = (v) => (v === undefined || v === null ? '' : String(v).replace(/[0-9]/g, (d) => FA[+d]));

const getUserDisplayName = (u) => {
  if (!u) return 'دانش‌آموز';
  if (u.firstName && u.lastName) return `${u.firstName} ${u.lastName}`.trim();
  if (u.firstName) return u.firstName;
  if (u.lastName) return u.lastName;
  if (u.name) return u.name;
  if (u.fullName) return u.fullName;
  if (u.username) return u.username;
  return 'دانش‌آموز';
};

const getSubjectIcon = (subject) => {
  const map = {
    'ریاضی': '🔬', 'فیزیک': '⚡', 'شیمی': '🧪', 'زیست': '🧬',
    'فارسی': '📜', 'عربی': '🌙', 'زبان': '🌍', 'دینی': '📖',
    'قرآن': '📿', 'علوم': '🔭', 'مطالعات': '🗺️', 'هدیه': '🎁',
  };
  return map[subject] || '📝';
};

const getSubjectEmoji = (subject) => {
  const map = { 'ریاضی': '🔬', 'علوم': '🔭', 'فارسی': '📜', 'عربی': '🌙', 'زبان': '🌍', 'مطالعات': '🗺️' };
  return map[subject] || '📚';
};

const getAccent = (subject) => {
  const m = {
    'ریاضی': 'var(--s-math)', 'علوم': 'var(--s-science)', 'فارسی': 'var(--s-farsi)',
    'عربی': 'var(--s-arabic)', 'زبان': 'var(--s-english)', 'مطالعات': 'var(--s-studies)',
  };
  return m[subject] || 'var(--purple)';
};

const getGlow = (subject) => {
  const m = {
    'ریاضی': 'var(--s-math-g)', 'علوم': 'var(--s-science-g)', 'فارسی': 'var(--s-farsi-g)',
    'عربی': 'var(--s-arabic-g)', 'زبان': 'var(--s-english-g)', 'مطالعات': 'var(--s-studies-g)',
  };
  return m[subject] || 'rgba(168,85,247,0.35)';
};

// ✅ Helper: بررسی دقیق flag (فقط مقادیر "واقعاً true")
const isTrulyTrue = (v) =>
  v === true || v === 1 || v === 'true' || v === '1';

// ═══════════════════════════════════════════════════════════════
//  BOOK STATS + BUILDERS
// ═══════════════════════════════════════════════════════════════
const getBookStats = (book) => {
  let total = 0, completed = 0, active = 0, activeChapters = 0;
  book.chapters.forEach((c) => {
    let chapterHasActive = false;
    c.exams.forEach((e) => {
      total++;
      if (e.status === 'completed') completed++;
      if (e.section === 'active') {
        active++;
        chapterHasActive = true;
      }
    });
    if (chapterHasActive) activeChapters++;
  });
  return { total, completed, active, activeChapters, chapters: book.chapters.length };
};

// تبدیل exam خام به مدل کارت + محاسبه status واقعی
const computeExamStatus = (exam, { userId, coins, paidExams, seenIds, now }) => {
  const result = exam.results?.find((r) => idsEqual(r.studentId, userId));
  const isFinalSubmit = result?.isFinalSubmit === true;
  const isAbsent = result?.isAbsent === true;
  const needsCorrection = result && result.isEssayGraded === false;
  const maxScore = (exam.questions || []).reduce((s, q) => s + toNumber(q.score, 1), 0);
  const requiredCoins = toNumber(exam.requiredCoins, 0);
  const isPaid = (paidExams || []).includes(exam.id);
  const isLockedByCoins = requiredCoins > 0 && !isPaid && coins < requiredCoins;

  let status = 'active';
  let section = 'active';

  if (isAbsent || isFinalSubmit) {
    status = 'completed';
    section = 'history';
  } else if (needsCorrection) {
    status = 'pending';
    section = 'history';
  } else if (exam.statusType === 'closed' || exam.statusType === 'correction') {
    status = 'expired';
    section = 'history';
  } else if (exam.statusType === 'pending') {
    status = 'upcoming';
    section = 'hidden';
  } else if (isLockedByCoins) {
    status = 'locked';
    section = 'active';
  }

  const userScore = result ? toNumber(result.score, 0) : 0;
  const isNew = exam.createdAt && !(seenIds || []).includes(exam.id)
    && moment(exam.createdAt).isAfter(now.clone().subtract(24, 'hours'));
  const isHot = (exam.participants || 0) >= 1000 || exam.forceHot === true;

  // ✅ منبع واحد حقیقت برای پاداش
  const isChallengeExam = isTrulyTrue(exam.isChallenge);
  let rewardCoins = 0;
  let rewardXp = 0;

  if (isChallengeExam) {
    // برای چالش: بیشترین مقدار از تیرهای مختلف
    rewardCoins = Math.max(
      toNumber(exam.coinsExcellent, 0),
      toNumber(exam.coinsVeryGood, 0),
      toNumber(exam.coinsGood, 0),
      toNumber(exam.coinsNeedsWork, 0),
      toNumber(exam.coinsUnacceptable, 0),
      toNumber(exam.coinsFailed, 0)
    );
    rewardXp = Math.max(
      toNumber(exam.xpExcellent, 0),
      toNumber(exam.xpVeryGood, 0),
      toNumber(exam.xpGood, 0),
      toNumber(exam.xpNeedsWork, 0),
      toNumber(exam.xpUnacceptable, 0),
      toNumber(exam.xpFailed, 0)
    );
  } else {
    // برای آزمون عادی: بدون هیچ fallback
    rewardCoins = toNumber(exam.rewardCoins, 0);
    rewardXp = toNumber(exam.rewardXp, 0);
  }

  const hasReward = rewardCoins > 0 || rewardXp > 0;

  return {
    id: exam.id,
    subject: exam.subject || '',
    mascot: getSubjectIcon(exam.subject),
    title: exam.title || 'آزمون بدون عنوان',
    description: exam.description || 'این آزمون برای تمرین و تثبیت مطالب طراحی شده است.',
    questions: Array.isArray(exam.questions) ? exam.questions.length : (typeof exam.questions === 'number' ? exam.questions : 0),
    duration: exam.duration || 0,
    coins: rewardCoins,
    xp: rewardXp,
    hasReward,
    requiredCoins,
    status,
    section,
    statusType: exam.statusType || 'active',
    score: userScore,
    maxScore,
    submittedAt: result?.submittedAt,
    isNew: !!isNew,
    isHot: !!isHot,
    teacherName: exam.teacherName || '—',
    _originalExam: exam,
  };
};

// ساخت BOOKS از exams
const buildBooks = (exams, opts) => {
  const bookMap = new Map();
  exams.forEach((exam) => {
    if (isTrulyTrue(exam.isChallenge)) return;
    const bookKey = exam.book || '__no_book__';
    const bookTitle = exam.book || 'بدون کتاب';
    if (!bookMap.has(bookKey)) {
      bookMap.set(bookKey, {
        id: `book-${bookKey}`,
        subject: exam.subject || '',
        title: bookTitle,
        emoji: getSubjectIcon(exam.subject),
        teamName: opts.isGroup ? (exam.teamName || '—') : undefined,
        teacherName: opts.isGroup ? (exam.teacherName || '—') : undefined,
        chaptersMap: new Map(),
      });
    }
    const book = bookMap.get(bookKey);
    const chKey = exam.chapter || 'بدون فصل';
    if (!book.chaptersMap.has(chKey)) {
      book.chaptersMap.set(chKey, { name: chKey, exams: [] });
    }
    book.chaptersMap.get(chKey).exams.push(computeExamStatus(exam, opts));
  });

  const subjectOrder = ['ریاضی', 'علوم', 'فیزیک', 'شیمی', 'زیست', 'فارسی', 'عربی', 'زبان', 'دینی', 'قرآن', 'مطالعات', 'هدیه'];
  return [...bookMap.values()]
    .map((b) => ({
      ...b,
      chapters: [...b.chaptersMap.values()].sort((a, b) => {
        const numA = parseInt(a.name.replace(/\D/g, ''), 10) || 0;
        const numB = parseInt(b.name.replace(/\D/g, ''), 10) || 0;
        return numA - numB || a.name.localeCompare(b.name);
      }),
    }))
    .sort((a, b) => {
      const ia = subjectOrder.indexOf(a.subject);
      const ib = subjectOrder.indexOf(b.subject);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib) || a.title.localeCompare(b.title);
    });
};

// ═══════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════
const ExamsStudent = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, updateUser } = useAuth();
  const coinCtx = useCoins() || {};
  const coins = coinCtx.coins ?? user?.coins ?? 0;
  const removeCoins = typeof coinCtx.removeCoins === 'function' ? coinCtx.removeCoins : () => 0;

  const {
    exams = [],
    getExamsForStudent,
    getGlobalExams,
    checkUserMembership,
  } = useExams() || {};

  const currentStudentId = user?.id;

  // ═══ STATE ═══
  const urlTab = searchParams.get('tab');
  const initialTab = ['public', 'group', 'history', 'performance'].includes(urlTab) ? urlTab : 'public';

  const [activeTab, setActiveTab] = useState(initialTab);
  const [isDark, setIsDark] = useState(() => {
  try { return localStorage.getItem('navbar2-drawer-dark') === '1'; } catch { return false; }
});
  const [publicBookId, setPublicBookId] = useState(null);
  const [publicChapter, setPublicChapter] = useState(null);
  const [groupBookId, setGroupBookId] = useState(null);
  const [groupChapter, setGroupChapter] = useState(null);
  const [historySubTab, setHistorySubTab] = useState('all');
  const [historySearch, setHistorySearch] = useState('');
  const [perfPeriod, setPerfPeriod] = useState('month');
  const [sheetExamId, setSheetExamId] = useState(null);
  const [resultExamId, setResultExamId] = useState(null);
  const [coinModalExamId, setCoinModalExamId] = useState(null);

  // Team data
const [studentTeamId, setStudentTeamId] = useState(null);
const [studentTeamIds, setStudentTeamIds] = useState([]);
const [teamTeacherIds, setTeamTeacherIds] = useState([]);
const [teamMembers, setTeamMembers] = useState([]);
  const [, setLoadingTeam] = useState(true);
  const [, setIsMember] = useState(false);
  const teamMembersRef = useRef([]);

  // دیگر نیازی به seenIds برای این نسخه نیست ولی برای isNew نگه می‌داریم
  const seenIds = useMemo(
    () => (Array.isArray(user?.publicExamsSeenIds) ? user.publicExamsSeenIds : []),
    [user?.publicExamsSeenIds]
  );

    const streak = useMemo(
    () => computeStreak(currentStudentId, exams || []),
    [currentStudentId, exams]
  );

  const [now, setNow] = useState(moment());
  const hasLoadedOnce = useRef(false);

  // ═══ STUDENT CONTEXT ═══
  const studentField = useMemo(() => {
    const raw = user?.field || (() => {
      try {
        if (Array.isArray(window.__allUsersCache)) {
          const found = window.__allUsersCache.find((u) => u?.id === currentStudentId);
          return found?.field || null;
        }
        return null;
      } catch { return null; }
    })();
    return raw ? normalizeFieldId(raw) || null : null;
  }, [user?.field, currentStudentId]);

  const studentGrade = useMemo(() => {
    const raw = user?.grade || (() => {
      try {
        const stored = localStorage.getItem('users');
        if (!stored) return null;
        const users = JSON.parse(stored);
        return users.find((u) => u?.id === currentStudentId)?.grade || null;
      } catch { return null; }
    })();
    return raw || null;
  }, [user?.grade, currentStudentId]);

  const studentGradeId = useMemo(() => (studentGrade ? normalizeGradeId(studentGrade) || studentGrade : null), [studentGrade]);
  const studentGradeTitle = useMemo(() => (studentGradeId ? getGrade(studentGradeId)?.title || studentGradeId : ''), [studentGradeId]);

  // ═══ LOAD TEAM (از localStorage — مثل قبل) ═══
  // ═══════════════════════════════════════════════════════════════
  // ✅ عضویت — سرور منبع حقیقت، localStorage فقط fallback
  // ═══════════════════════════════════════════════════════════════
const loadTeamData = useCallback(async () => {
  if (!currentStudentId) {
    setStudentTeamId(null);
    setStudentTeamIds([]);
    setTeamTeacherIds([]);
    setIsMember(false);
    setTeamMembers([]);
    teamMembersRef.current = [];
    setLoadingTeam(false);
    return;
  }

  setLoadingTeam(true);
  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  // ─── ۱. خواندن عضویت‌های active از سرور ───
  let memberships = null; // null = نامشخص | [] = خالی | [...] = معتبر
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    const res = await fetch(
      `${API_BASE}/teamMembers?studentId=${encodeURIComponent(currentStudentId)}`,
      { signal: controller.signal }
    );
    clearTimeout(timeoutId);
    if (res.ok) {
      const data = await res.json();
      memberships = (Array.isArray(data) ? data : []).filter(
        (m) => m && idsEqual(m.studentId, currentStudentId) && m.status === 'active'
      );
    }
  } catch { /* fallback */ }

  // ─── ۲. Fallback قطعی: اگر سرور خطا داد یا آرایه خالی برگرداند ───
  if (!memberships || memberships.length === 0) {
    try {
      const stored = localStorage.getItem('teamMembers');
      if (stored) {
        const parsed = JSON.parse(stored);
        const mine = (Array.isArray(parsed) ? parsed : []).filter(
          (m) => m && idsEqual(m.studentId, currentStudentId) && m.status === 'active'
        );
        if (mine.length > 0) memberships = mine;
      }
    } catch { /* ignore */ }
  }

  // ─── ۳. اگر هیچ تیمی پیدا نشد، صریحاً پاک کن ───
  if (!memberships || memberships.length === 0) {
    setStudentTeamId(null);
    setStudentTeamIds([]);
    setTeamTeacherIds([]);
    setIsMember(false);
    setTeamMembers([]);
    teamMembersRef.current = [];
    setLoadingTeam(false);
    return;
  }

  // ─── ۴. استخراج teamIds یکتا ───
  const uniqueTeamIds = [];
  const seen = new Set();
  memberships.forEach((m) => {
    if (m.teamId && !seen.has(String(m.teamId))) {
      seen.add(String(m.teamId));
      uniqueTeamIds.push(m.teamId);
    }
  });

  // ─── ۵. پیدا کردن teacherId برای هر تیم ───
  const teacherIds = [];
  const seenTeachers = new Set();
  for (const tid of uniqueTeamIds) {
    let teacherId = null;

    try {
      const storedTeam = localStorage.getItem('team');
      if (storedTeam) {
        const t = JSON.parse(storedTeam);
        if (t && idsEqual(t.id, tid) && t.teacherId) teacherId = t.teacherId;
      }
    } catch { /* ignore */ }

    if (!teacherId) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000);
        const res = await fetch(`${API_BASE}/teams/${encodeURIComponent(tid)}`, { signal: controller.signal });
        clearTimeout(timeoutId);
        if (res.ok) {
          const t = await res.json();
          if (t && t.teacherId) teacherId = t.teacherId;
        }
      } catch { /* ignore */ }
    }

    if (teacherId && !seenTeachers.has(String(teacherId))) {
      seenTeachers.add(String(teacherId));
      teacherIds.push(teacherId);
    }
  }

  // ─── ۶. اعضای همه‌ی تیم‌های دانش‌آموز ───
  let allActiveMembers = [];
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    const res = await fetch(`${API_BASE}/teamMembers`, { signal: controller.signal });
    clearTimeout(timeoutId);
    if (res.ok) {
      const all = await res.json();
      allActiveMembers = (Array.isArray(all) ? all : []).filter(
        (m) => m && m.status === 'active' && uniqueTeamIds.some((tid) => idsEqual(m.teamId, tid))
      );
    }
  } catch {
    try {
      const stored = localStorage.getItem('teamMembers');
      if (stored) {
        const parsed = JSON.parse(stored);
        allActiveMembers = (Array.isArray(parsed) ? parsed : []).filter((m) => m && m.status === 'active');
      }
    } catch { /* ignore */ }
  }

  // ─── ۷. ست کردن stateها ───
  setStudentTeamIds(uniqueTeamIds);
  setTeamTeacherIds(teacherIds);
  setStudentTeamId(uniqueTeamIds[0] || null);
  setIsMember(true);
  setTeamMembers(allActiveMembers);
  teamMembersRef.current = allActiveMembers;
  setLoadingTeam(false);
}, [currentStudentId]);

  useEffect(() => {
    // ✅ همیشه در mount و هر بار userId تغییر کرد
    loadTeamData();

    const onStorage = (e) => {
      if (!e.key) return;
      if (['teamMembers', 'team', 'users', 'exams'].includes(e.key)) loadTeamData();
    };
    const onFocus = () => loadTeamData();
    const onVis = () => { if (!document.hidden) loadTeamData(); };

    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);

    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [loadTeamData]);

  useEffect(() => {
    const t = setInterval(() => setNow(moment()), 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
  try { localStorage.setItem('navbar2-drawer-dark', isDark ? '1' : '0'); } catch {}
}, [isDark]);

useEffect(() => {
  const readTheme = () => {
    try {
      const cur = localStorage.getItem('navbar2-drawer-dark') === '1';
      setIsDark(prev => (prev !== cur ? cur : prev));
    } catch {}
  };
  const handleStorage = (e) => { if (e.key === 'navbar2-drawer-dark') readTheme(); };
  const handleThemeEvent = () => readTheme();
  const handleVisibility = () => { if (!document.hidden) readTheme(); };
  window.addEventListener('storage', handleStorage);
  window.addEventListener('theme-change', handleThemeEvent);
  document.addEventListener('visibilitychange', handleVisibility);
  return () => {
    window.removeEventListener('storage', handleStorage);
    window.removeEventListener('theme-change', handleThemeEvent);
    document.removeEventListener('visibilitychange', handleVisibility);
  };
}, []);

  // ═══════════════════════════════════════════════════════════════
  // 🎯 منبع واحد حقیقت: ID همه‌ی آزمون‌های عمومی
  // از دو منبع جمع می‌شود تا هیچ آزمون عمومی از قلم نیفتد
  // ═══════════════════════════════════════════════════════════════
const globalExamIds = useMemo(() => {
  const fromContext = typeof getGlobalExams === 'function' ? getGlobalExams() : [];
  return new Set(
    (fromContext || []).filter((e) => e && e.id).map((e) => String(e.id))
  );
}, [getGlobalExams]);

  // ═══ BOOKS (دادهٔ واقعی) ═══
const publicBooks = useMemo(() => {
  const filtered = (exams || []).filter((e) => {
    if (!e || !e.id) return false;
    if (!globalExamIds.has(String(e.id))) return false;
    if (isTrulyTrue(e.isChallenge)) return false;
    if (isTrulyTrue(e.isStepQuiz)) return false;
    if (e.statusType === 'archived') return false;

    // ✅ فیلتر پایه: فقط وقتی هر دو طرف مقدار دارند مقایسه کن
    if (studentGradeId && e.grade) {
      const eg = normalizeGradeId(e.grade);
      if (eg) {
        if (eg !== studentGradeId) return false;
      } else {
        // نرمال نشد ولی شاید رشته‌ای برابر باشد
        if (String(e.grade) !== String(studentGradeId)) return false;
      }
    }
    // ✅ فیلتر رشته: فقط وقتی آزمون رشته دارد و دانش‌آموز رشته دارد
    if (studentField && e.field && e.field !== studentField) return false;

    return true;
  });
  return buildBooks(filtered, {
    isGroup: false,
    userId: currentStudentId,
    coins,
    paidExams: user?.paidExams || [],
    seenIds,
    now,
  });
}, [exams, globalExamIds, studentGradeId, studentField, currentStudentId, coins, user?.paidExams, seenIds, now]);

const groupBooks = useMemo(() => {
  if (!currentStudentId) return [];
  if (studentTeamIds.length === 0 && teamTeacherIds.length === 0) return [];

  const teamExams = (exams || []).filter((e) => {
    if (!e || !e.id) return false;
    if (globalExamIds.has(String(e.id))) return false;
    if (isTrulyTrue(e.isGlobal)) return false;
    if (isTrulyTrue(e.isChallenge)) return false;
    if (isTrulyTrue(e.isStepQuiz)) return false;
    if (e.statusType === 'archived') return false;

    // ۱. تطبیق دقیق teamId
    if (studentTeamIds.some((tid) => idsEqual(e.teamId, tid))) return true;

    // ۲. fallback: آزمون بدون teamId ولی مال معلم یکی از تیم‌ها
    if ((!e.teamId || e.teamId === 'no-group') && teamTeacherIds.some((tt) => idsEqual(e.teacherId, tt))) {
      return true;
    }

    // ۳. تاریخچه: دانش‌آموز در این آزمون نتیجه دارد (حتی اگر از تیم قبلی باشد)
    if (Array.isArray(e.results) && e.results.some((r) => r && idsEqual(r.studentId, currentStudentId))) {
      return true;
    }

    return false;
  });

  return buildBooks(teamExams, {
    isGroup: true,
    userId: currentStudentId,
    coins,
    paidExams: user?.paidExams || [],
    seenIds,
    now,
  });
}, [exams, globalExamIds, studentTeamIds, teamTeacherIds, currentStudentId, coins, user?.paidExams, seenIds, now]);

  // ═══ HISTORY (مشتق از BOOKS) ═══
  const historyAll = useMemo(() => {
    const all = [];
    publicBooks.forEach((b) =>
      b.chapters.forEach((c) =>
        c.exams.forEach((e) => {
          if (e.section === 'history') {
            all.push({ ...e, bookTitle: b.title, chapterName: c.name, source: 'public' });
          }
        })
      )
    );
    groupBooks.forEach((b) =>
      b.chapters.forEach((c) =>
        c.exams.forEach((e) => {
          if (e.section === 'history') {
            all.push({ ...e, bookTitle: b.title, chapterName: c.name, source: 'group' });
          }
        })
      )
    );
    const getSortTime = (e) => {
      const t = e.submittedAt ? new Date(e.submittedAt).getTime() : 0;
      if (t > 0) return t;
      const orig = e._originalExam;
      const d = orig?.date || orig?.startTime;
      return d ? new Date(d).getTime() : 0;
    };
    return all.sort((a, b) => getSortTime(b) - getSortTime(a));
  }, [publicBooks, groupBooks]);

  // ═══ PERFORMANCE DATA (فیلترشده توسط period) ═══
  const perfExams = useMemo(() => {
    let list = historyAll.filter((e) => e.status === 'completed');
    if (perfPeriod === 'week') {
      const dow = moment().day(); // 0=Sun,6=Sat
      const daysBack = (dow + 1) % 7;
      const start = moment().subtract(daysBack, 'days').startOf('day');
      list = list.filter((e) => e.submittedAt && moment(e.submittedAt).isAfter(start));
    } else if (perfPeriod === 'month') {
      const start = moment().startOf('jMonth');
      list = list.filter((e) => e.submittedAt && moment(e.submittedAt).isAfter(start));
    }
    return list;
  }, [historyAll, perfPeriod]);

  // ═══ FIND EXAM BY ID ═══
  const findExamById = useCallback(
    (id) => {
      for (const b of publicBooks) for (const c of b.chapters) {
        const e = c.exams.find((x) => x.id === id);
        if (e) return { exam: e, book: b, chapter: c };
      }
      for (const b of groupBooks) for (const c of b.chapters) {
        const e = c.exams.find((x) => x.id === id);
        if (e) return { exam: e, book: b, chapter: c };
      }
      const h = historyAll.find((x) => x.id === id);
      if (h) return { exam: h, book: { title: h.bookTitle }, chapter: { name: h.chapterName } };
      return null;
    },
    [publicBooks, groupBooks, historyAll]
  );

  // ═══ ACTIONS ═══
  const switchTab = useCallback((tab) => {
    setActiveTab(tab);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const openBook = useCallback((bookId, mode) => {
    if (mode === 'public') { setPublicBookId(bookId); setPublicChapter(null); }
    else { setGroupBookId(bookId); setGroupChapter(null); }
    window.scrollTo({ top: 100, behavior: 'smooth' });
  }, []);

  const selectChapter = useCallback((chIdx, mode) => {
    if (mode === 'public') setPublicChapter(chIdx);
    else setGroupChapter(chIdx);
  }, []);

  const openSheet = useCallback((id) => setSheetExamId(id), []);
  const closeSheet = useCallback(() => setSheetExamId(null), []);
  const openResult = useCallback((id) => {
    const found = findExamById(id);
    if (!found) return;
    // بررسی وجود نتیجه در همه منابع ممکن
    const candidates = [
      found.exam._originalExam,
      found.exam,
      (typeof getGlobalExams === 'function' ? getGlobalExams() : []).find((e) => idsEqual(e.id, id)),
      (exams || []).find((e) => idsEqual(e.id, id)),
    ].filter(Boolean);
    const hasResult = candidates.some((c) =>
      c.results?.some((r) => idsEqual(r.studentId, currentStudentId))
    );
    if (!hasResult) {
      if (found.exam.status === 'expired') {
        window.alert('⏰ این آزمون بسته شده و شما در آن شرکت نکرده‌اید.');
        return;
      }
      setSheetExamId(id);
      setResultExamId(null);
      return;
    }
    setSheetExamId(null);
    setResultExamId(id);
  }, [findExamById, getGlobalExams, exams, currentStudentId]);
  const closeResult = useCallback(() => setResultExamId(null), []);
  const openCoinModal = useCallback((id) => setCoinModalExamId(id), []);
  const closeCoinModal = useCallback(() => setCoinModalExamId(null), []);

  // ═══ START EXAM (با کسر سکه و ثبت paidExams) ═══
  const handleStartExam = useCallback((exam) => {
    if (!currentStudentId) {
      if (window.confirm('برای شرکت در آزمون باید وارد حساب خود شوید. آیا می‌روید؟')) navigate('/login');
      return;
    }
    const requiredCoins = toNumber(exam.requiredCoins, 0);
    const paidExams = user?.paidExams || [];

    if (requiredCoins > 0 && !paidExams.includes(exam.id)) {
      if (coins < requiredCoins) { openCoinModal(exam.id); return; }
      const ok = window.confirm(
        `این آزمون ${toFa(requiredCoins)} سکه هزینه دارد.\nموجودی شما: ${toFa(coins)} سکه\n\nآیا موافقت می‌کنید؟`
      );
      if (!ok) return;
      const removed = removeCoins(requiredCoins);
      if (removed <= 0) { window.alert('⚠️ خطا در کسر سکه. لطفاً دوباره تلاش کنید.'); return; }
      if (typeof updateUser === 'function') updateUser({ paidExams: [...paidExams, exam.id] });
    }
    navigate(`/exams/take/${exam.id}`);
  }, [currentStudentId, coins, removeCoins, user?.paidExams, updateUser, navigate, openCoinModal]);

  // ═══ EFFECTS ═══
  useEffect(() => {
    const isAnyOpen = sheetExamId || resultExamId || coinModalExamId;
    document.body.style.overflow = isAnyOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [sheetExamId, resultExamId, coinModalExamId]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') { closeSheet(); closeResult(); closeCoinModal(); }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [closeSheet, closeResult, closeCoinModal]);

  // ═══════════════════════════════════════════════════════════════
  //  RENDER SECTIONS
  // ═══════════════════════════════════════════════════════════════

  // --- Topbar + Tabs ---
const renderTopbar = () => null;

const renderTabsNav = () => (
  <nav className="tabs">
    {[
      { key: 'public', icon: '🌍', label: 'عمومی' },
      { key: 'group', icon: '👥', label: 'گروهی' },
      { key: 'history', icon: '📊', label: 'کارنامه‌' },
      { key: 'performance', icon: '📈', label: 'عملکرد' },
    ].map((t) => (
      <button key={t.key} className={`tab ${activeTab === t.key ? 'active' : ''}`} onClick={() => switchTab(t.key)}>
        <span className="tab-ico">{t.icon}</span>
        <span className="tab-lbl">{t.label}</span>
      </button>
    ))}
  </nav>
);

  const renderGreeting = () => (
    <>
      <section className="greeting">
        <div className="greeting-text">
          <h1>آزمون‌ها و ماموریت‌های ویژه</h1>
        </div>
      </section>
      <div className="grade-badge">
        {(() => {
          const activeBooks = publicBooks.filter((b) => getBookStats(b).active > 0);
          const activeExams = publicBooks.reduce((s, b) => s + getBookStats(b).active, 0);
          return `${studentGradeTitle ? ` ${studentGradeTitle}` : 'دانش‌آموز'} · ${toFa(activeBooks.length)} کتاب · ${toFa(activeExams)} آزمون فعال`;
        })()}
      </div>
    </>
  );

  // --- Book Card ---
  const renderBookCard = (book, mode) => {
    const stats = getBookStats(book);
    const pct = stats.total > 0 ? Math.round((stats.completed / stats.total) * 100) : 0;
    const teacherLine = mode === 'group' && (book.teamName || book.teacherName);
    return (
      <article
        key={book.id}
        className="book-card"
        data-subject={book.subject}
        onClick={() => openBook(book.id, mode)}
      >
        <div className="book-cover">{book.emoji}</div>
        <h3 className="book-title">{book.title}</h3>
        {teacherLine ? (
          <p className="book-meta">👨‍🏫 {book.teacherName || book.teamName} · {toFa(stats.active)} آزمون فعال</p>
        ) : (
          <p className="book-meta">{toFa(stats.activeChapters)} فصل · {toFa(stats.active)} آزمون فعال</p>
        )}
        <div className="book-progress">
          {/*<div className="book-progress-label">
            <span>پیشرفت</span>
            <strong>{toFa(pct)}٪</strong>
          </div>
          <div className="book-progress-bar">
            <div className="book-progress-fill" style={{ width: `${pct}%` }} />
          </div>*/}
        </div>
        <div className="book-cta">مشاهده →</div>
      </article>
    );
  };

  // --- Book List ---
  const renderBookList = (books, mode) => {
    const isGroup = mode === 'group';
    const title = isGroup ? '🏫 کتاب‌های کلاسی من' : '📚 کتاب‌های درسی من';
    const subtitle = isGroup
      ? 'آزمون‌های تعیین‌شده توسط معلم'
      : `کتاب‌های ${studentGradeTitle || 'شما'}، هر کدام با فصل‌ها و آزمون‌های جداگانه`;
    const activeBooks = books.filter((b) => getBookStats(b).active > 0);

    // ✅ حالت ۱: دانش‌آموز در هیچ تیمی نیست
    if (isGroup && !studentTeamId) {
      return (
        <div className="tab-content" key={`booklist-${mode}`}>
          <div className="section">
            <div className="section-head">
              <div>
                <h2>{title}</h2>
                <div className="section-sub">{subtitle}</div>
              </div>
            </div>
            <div className="empty-history">
              <div className="eh-icon">🔒</div>
              <p style={{ fontSize: 16, fontWeight: 800, marginBottom: 10 }}>
                شما در هیچ گروهی عضو نیستید
              </p>
              <p style={{ fontSize: 13, color: 'var(--text-3)', lineHeight: 1.9, maxWidth: 380, margin: '0 auto' }}>
                برای دیدن آزمون‌های معلمتان، ابتدا باید معلم شما را به گروه اضافه کند.<br />
                <strong>فقط معلم می‌تواند دعوت‌نامه ارسال کند.</strong>
              </p>
              <button
                type="button"
                onClick={() => loadTeamData()}
                style={{
                  marginTop: 20,
                  padding: '10px 20px',
                  borderRadius: 20,
                  border: 'none',
                  background: 'linear-gradient(135deg, var(--purple), var(--purple-deep))',
                  color: '#fff',
                  fontWeight: 800,
                  fontSize: 13,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                🔄 بررسی مجدد
              </button>
            </div>
          </div>
        </div>
      );
    }

    // ✅ حالت ۲: در تیم هست ولی آزمونی ندارد → پیام موجود
    // (fall through به کد فعلی که پیام «هیچ آزمون فعالی از طرف معلم» می‌دهد)

    return (
      <div className="tab-content" key={`booklist-${mode}`}>
        <div className="section">
          <div className="section-head">
            <div>
              <h2>{title}</h2>
              <div className="section-sub">{subtitle}</div>
            </div>
          </div>
          {activeBooks.length > 0 ? (
            <div className="book-grid">
              {activeBooks.map((b) => renderBookCard(b, mode))}
            </div>
          ) : (
            <div className="empty-history">
              <div className="eh-icon">📭</div>
              <p>{isGroup ? 'هیچ آزمون فعالی از طرف معلم برایت تعیین نشده' : 'هیچ آزمون فعالی برای نمایش موجود نیست'}</p>
              <p style={{ fontSize: 12, color: 'var(--text-4)', marginTop: 8 }}>
                آزمون‌های تکمیل‌شده در بخش «کارنامه‌ها» قابل مشاهده هستند.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // --- Book Detail ---
  const renderBookDetail = (book, mode) => {
    if (!book) {
      return (
        <div className="tab-content">
          <div className="empty-history">
            <div className="eh-icon">📭</div>
            <p>کتاب یافت نشد</p>
          </div>
        </div>
      );
    }
    const stats = getBookStats(book);
    const isGroup = mode === 'group';
    const chapterIdx = mode === 'public' ? publicChapter : groupChapter;
    const teacherInfo = isGroup ? ` · معلم: ${book.teacherName || '—'}` : '';

    let exams = [];
    if (chapterIdx === null) {
      book.chapters.forEach((c) => {
        exams = exams.concat(c.exams.filter((e) => e.section === 'active'));
      });
    } else {
      exams = (book.chapters[chapterIdx]?.exams || []).filter((e) => e.section === 'active');
    }

    return (
      <div className="tab-content" key={`bookdetail-${book.id}-${chapterIdx}`}>
        <div className="breadcrumb">
          <button className="breadcrumb-back" onClick={() => openBook(null, mode)}>← همه کتاب‌ها</button>
          <span className="breadcrumb-arrow">›</span>
          <span className="breadcrumb-current">{book.title}</span>
        </div>

        <div className="book-header" data-subject={book.subject}>
          <div className="book-header-icon">{book.emoji}</div>
          <div className="book-header-info">
            <h2>{book.title}</h2>
            <p>{book.subject}{teacherInfo} · {toFa(stats.activeChapters)} فصل · {toFa(stats.active)} آزمون فعال</p>
            <div className="book-header-stats">
              <span>🎯 {toFa(stats.active)} فعال</span>
              <span>✅ {toFa(stats.completed)} تکمیل شده</span>
            </div>
          </div>
        </div>

        <div className="chapter-pills">
          <button
            className={`chapter-pill ${chapterIdx === null ? 'active' : ''}`}
            onClick={() => selectChapter(null, mode)}
          >
            همه فصل‌ها
            <span className="cp-count">{toFa(stats.active)}</span>
          </button>
          {book.chapters.map((c, i) => {
            const activeCount = c.exams.filter((e) => e.section === 'active').length;
            if (activeCount === 0) return null;
            return (
              <button
                key={i}
                className={`chapter-pill ${chapterIdx === i ? 'active' : ''}`}
                onClick={() => selectChapter(i, mode)}
              >
                {c.name.split(':')[0]}
                <span className="cp-count">{toFa(activeCount)}</span>
              </button>
            );
          })}
        </div>

        <div className="section">
          <div className="section-head">
            <h2>
              {chapterIdx === null
                ? '📋 همه آزمون‌ها'
                : `📖 آزمون‌های ${book.chapters[chapterIdx]?.name || ''}`}
            </h2>
            <span style={{ fontSize: 12, color: 'var(--text-3)', fontWeight: 800 }}>
              {toFa(exams.length)} آزمون
            </span>
          </div>
          <div className="cards-grid">
            {exams.length > 0 ? (
              exams.map((e) => renderExamCard(e, mode))
            ) : (
              <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: 40, color: 'var(--text-3)' }}>
                📭 آزمونی در این فصل موجود نیست
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // --- Exam Card ---
  const renderExamCard = (exam, mode) => {
    // Badge
    let badgeHTML;
    if (exam.status === 'completed') {
      const pct = exam.maxScore > 0 ? Math.round((exam.score / exam.maxScore) * 100) : 0;
      badgeHTML = (
        <span className={`card-badge ${pct >= 50 ? 'badge-pass' : 'badge-fail'}`}>
          {pct >= 50 ? '✅ قبول' : '❌ مردود'}
        </span>
      );
    } else if (exam.status === 'pending') {
      badgeHTML = <span className="card-badge badge-pending">⏳ تصحیح</span>;
    } else if (exam.status === 'locked') {
      badgeHTML = <span className="card-badge badge-lock">🔒 {toFa(exam.requiredCoins || 0)}</span>;
    } else if (exam.isHot) {
      badgeHTML = <span className="card-badge badge-hot">🔥 داغ</span>;
    } else if (exam.isNew) {
      badgeHTML = <span className="card-badge badge-new">✨ جدید</span>;
    } else if (exam.hasReward && exam.coins > 0) {
      badgeHTML = <span className="card-badge badge-gold">🪙 {toFa(exam.coins)}</span>;
    } else if (exam.hasReward && exam.xp > 0) {
      badgeHTML = <span className="card-badge badge-gold">⭐ {toFa(exam.xp)}</span>;
    } else {
      badgeHTML = <span className="card-badge badge-free">🆓</span>;
    }

    // Progress (completed)
    let progressHTML = null;
    if (exam.status === 'completed') {
      const pct = exam.maxScore > 0 ? Math.round((exam.score / exam.maxScore) * 100) : 0;
      const isPass = exam.score >= calculatePassThreshold(exam.maxScore);
      progressHTML = (
        <div className="card-progress">
          <div className="card-progress-label">
            <span>نمره</span>
            <span style={{ color: isPass ? 'var(--mint-bright)' : 'var(--rose)' }}>
              {toFa(exam.score)}/{toFa(exam.maxScore)}
            </span>
          </div>
          <div className="card-progress-bar">
            <div
              className="card-progress-fill"
              style={{ width: `${pct}%`, background: isPass ? 'var(--mint)' : 'var(--rose)' }}
            />
          </div>
        </div>
      );
    }

    // Button
    let btn;
    if (exam.status === 'completed') {
      btn = <button className="card-btn btn-result">🏆 کارنامه</button>;
    } else if (exam.status === 'pending') {
      btn = <button className="card-btn btn-disabled" disabled>⏳ در انتظار</button>;
    } else if (exam.status === 'locked') {
      btn = <button className="card-btn btn-disabled" disabled>🔒 قفل</button>;
    } else {
      btn = <button className="card-btn">🚀 شروع</button>;
    }

    const teacherLine = mode === 'group' && exam.teacherName && exam.teacherName !== '—'
      ? <div style={{ fontSize: 10, color: 'var(--text-4)', fontWeight: 700, marginBottom: 6 }}>👨‍🏫 {exam.teacherName}</div>
      : null;

    const handleCardClick = (e) => {
      if (e.target.closest('.card-btn')) return;
      openSheet(exam.id);
    };
    const handleBtnClick = (e) => {
      e.stopPropagation();
      if (exam.status === 'completed') return openResult(exam.id);
      if (exam.status === 'locked') return openCoinModal(exam.id);
      if (exam.status === 'pending') return;
      openSheet(exam.id);
    };

    return (
      <article
        key={exam.id}
        className={`exam-card ${exam.status === 'completed' ? 'is-completed' : ''} ${exam.status === 'pending' ? 'is-pending' : ''} ${exam.status === 'locked' ? 'is-locked' : ''}`}
        data-subject={exam.subject}
        onClick={handleCardClick}
      >
        <div className="card-top">
          <div className="card-mascot">{exam.mascot}</div>
          {badgeHTML}
        </div>
        {teacherLine}
        <h3 className="card-title">{exam.title}</h3>
        <p className="card-meta">{toFa(exam.questions)} سوال · {toFa(exam.duration)} دقیقه</p>
        {progressHTML}
        <div className="card-stats">
          <span>📝 {toFa(exam.questions)}</span>
          <span>⏱ {toFa(exam.duration)}′</span>
          {exam.hasReward && exam.coins > 0 && <span>🪙 {toFa(exam.coins)}</span>}
          {exam.hasReward && exam.xp > 0 && <span>⭐ {toFa(exam.xp)}</span>}
        </div>
        <div onClick={handleBtnClick} style={{ display: 'contents' }}>{btn}</div>
      </article>
    );
  };

  // --- History Tab ---
  const renderHistoryTab = () => {
    const filtered = historyAll.filter((e) => {
      if (historySubTab === 'public' && e.source !== 'public') return false;
      if (historySubTab === 'group' && e.source !== 'group') return false;
      if (historySearch) {
        const q = historySearch.toLowerCase();
        if (!e.title?.toLowerCase().includes(q) && !e.bookTitle?.toLowerCase().includes(q)) return false;
      }
      return true;
    });

    const completed = historyAll.filter((e) => e.status === 'completed');
    const passedCount = completed.filter((e) => e.maxScore > 0 && e.score >= calculatePassThreshold(e.maxScore)).length;
    const avg = completed.length > 0
      ? (completed.reduce((s, e) => s + (e.maxScore > 0 ? e.score / e.maxScore : 0), 0) / completed.length * 20).toFixed(1)
      : '—';

    return (
      <div className="tab-content">
        <div className="history-summary">
          <div className="hs-card hs-total">
            <div className="hs-icon">📊</div>
            <div className="hs-value">{toFa(historyAll.length)}</div>
            <div className="hs-label">کل آزمون‌ها</div>
          </div>
          <div className="hs-card hs-pass">
            <div className="hs-icon">✅</div>
            <div className="hs-value">{toFa(passedCount)}</div>
            <div className="hs-label">قبول شده</div>
          </div>
          <div className="hs-card hs-avg">
            <div className="hs-icon">⭐</div>
            <div className="hs-value">{avg}</div>
            <div className="hs-label">میانگین نمره</div>
          </div>
        </div>

        <div className="history-sub-tabs">
          <button className={`hsub-tab ${historySubTab === 'all' ? 'active' : ''}`} onClick={() => setHistorySubTab('all')}>همه</button>
          <button className={`hsub-tab ${historySubTab === 'public' ? 'active' : ''}`} onClick={() => setHistorySubTab('public')}>🌍 عمومی</button>
          <button className={`hsub-tab ${historySubTab === 'group' ? 'active' : ''}`} onClick={() => setHistorySubTab('group')}>👥 گروهی</button>
        </div>

        <div className="history-search">
          <span className="hsr-icon">🔍</span>
          <input
            type="text"
            placeholder="جستجو در نام آزمون یا کتاب..."
            value={historySearch}
            onChange={(e) => setHistorySearch(e.target.value)}
          />
        </div>

        <div className="history-list">
          {filtered.length > 0 ? (
            filtered.map((exam) => renderHistoryRow(exam))
          ) : (
            <div className="empty-history">
              <div className="eh-icon">📭</div>
              <p>هنوز آزمونی در این بخش شرکت نکردی</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderHistoryRow = (exam) => {
    const pct = exam.maxScore > 0 ? Math.round((exam.score / exam.maxScore) * 100) : 0;
    const isPass = exam.maxScore > 0 && exam.score >= calculatePassThreshold(exam.maxScore);
    let statusHTML, scoreDisplay;
    if (exam.status === 'expired') {
      statusHTML = <span className="history-row-status fail">🔒 بسته شده</span>;
      scoreDisplay = <span className="hrs-num" style={{ color: 'var(--text-3)' }}>—</span>;
    } else if (exam.status === 'pending') {
      statusHTML = <span className="history-row-status pending">⏳ تصحیح</span>;
      scoreDisplay = <span className="hrs-num" style={{ color: 'var(--amber-bright)' }}>…</span>;
    } else {
      statusHTML = (
        <span className={`history-row-status ${isPass ? 'pass' : 'fail'}`}>
          {isPass ? '✅ قبول' : '❌ مردود'}
        </span>
      );
      scoreDisplay = (
        <>
          <div className="hrs-num">{toFa(exam.score)}</div>
          <div className="hrs-max">از {toFa(exam.maxScore)}</div>
        </>
      );
    }
    const displayDate = exam.submittedAt || exam._originalExam?.date || exam._originalExam?.startTime;
    const dateStr = displayDate
      ? new Date(displayDate).toLocaleDateString('fa-IR', { month: 'short', day: 'numeric' })
      : '';
    return (
      <div
        key={exam.id}
        className="history-row"
        data-subject={exam.subject}
        onClick={() => openResult(exam.id)}
      >
        <div className="history-row-icon">{exam.mascot}</div>
        <div className="history-row-info">
          <div className="history-row-title">{exam.title}</div>
          <div className="history-row-meta">
            <span>📖 {exam.bookTitle}</span>
            <span>{exam.source === 'group' ? '👥 گروهی' : '🌍 عمومی'}</span>
            {dateStr && <span>📅 {dateStr}</span>}
          </div>
        </div>
        <div className="history-row-score">{scoreDisplay}</div>
        {statusHTML}
      </div>
    );
  };

  // --- Performance Tab ---
  const renderPerformanceTab = () => {
    const all = perfExams;
    const total = all.length;
    const passed = all.filter((e) => e.maxScore > 0 && e.score >= calculatePassThreshold(e.maxScore)).length;
    const avg = total > 0
      ? (all.reduce((s, e) => s + (e.maxScore > 0 ? e.score / e.maxScore : 0), 0) / total * 20).toFixed(1)
      : '—';

    // سطوح توزیع
    const levels = [
      { key: 'excellent', label: 'عالی', emoji: '🌟', color: 'var(--amber-bright)', min: 18 },
      { key: 'veryGood', label: 'خیلی خوب', emoji: '🎉', color: 'var(--mint-bright)', min: 16 },
      { key: 'good', label: 'خوب', emoji: '👍', color: 'var(--cyan-bright)', min: 12 },
      { key: 'needsWork', label: 'نیاز به تلاش', emoji: '💪', color: 'var(--amber)', min: 8 },
      { key: 'unacceptable', label: 'غیرقابل قبول', emoji: '📖', color: 'var(--pink)', min: 4 },
      { key: 'failed', label: 'مردود', emoji: '🔄', color: 'var(--rose)', min: 0 },
    ];
    const counts = levels.map((l) => ({ ...l, count: 0 }));
    all.forEach((e) => {
      const s = e.maxScore > 0 ? (e.score / e.maxScore) * 20 : 0;
      for (const l of counts) {
        if (s >= l.min) { l.count++; break; }
      }
    });
    const maxCount = Math.max(1, ...counts.map((c) => c.count));

    // تفکیک فصل
    const chapterMap = {};
    all.forEach((e) => {
      const bookTitle = e.bookTitle || '';
      const chapterName = e.chapterName || '';
      if (!chapterName) return;
      const key = `${bookTitle}|||${chapterName}`;
      if (!chapterMap[key]) {
        chapterMap[key] = { bookTitle, chapterName, total: 0, sum: 0 };
      }
      chapterMap[key].total++;
      chapterMap[key].sum += e.maxScore > 0 ? e.score / e.maxScore : 0;
    });
    const chapterData = Object.values(chapterMap)
      .map((d) => ({
        bookTitle: d.bookTitle,
        chapterName: d.chapterName,
        count: d.total,
        avgPct: Math.round((d.sum / d.total) * 100),
      }))
      .sort((a, b) => b.avgPct - a.avgPct);

    return (
      <div className="tab-content">
        <div className="perf-period">
          <button className={`perf-period-btn ${perfPeriod === 'week' ? 'active' : ''}`} onClick={() => setPerfPeriod('week')}>📅 این هفته</button>
          <button className={`perf-period-btn ${perfPeriod === 'month' ? 'active' : ''}`} onClick={() => setPerfPeriod('month')}>📆 این ماه</button>
          <button className={`perf-period-btn ${perfPeriod === 'all' ? 'active' : ''}`} onClick={() => setPerfPeriod('all')}>🗓 همه</button>
        </div>

        <div className="perf-cards">
          <div className="perf-card" data-color="amber">
            <div className="pc-icon">⭐</div>
            <div className="pc-value">{avg}</div>
            <div className="pc-label">میانگین نمره</div>
            <div className="pc-sub">از ۲۰</div>
          </div>
          <div className="perf-card" data-color="cyan">
            <div className="pc-icon">📊</div>
            <div className="pc-value">{toFa(total)}</div>
            <div className="pc-label">آزمون داده‌شده</div>
            <div className="pc-sub">تکمیل‌شده</div>
          </div>
          <div className="perf-card" data-color="mint">
            <div className="pc-icon">✅</div>
            <div className="pc-value">{toFa(passed)}</div>
            <div className="pc-label">قبول</div>
            <div className="pc-sub">{total > 0 ? `${toFa(Math.round((passed / total) * 100))}٪ موفقیت` : '—'}</div>
          </div>
          <div className="perf-card" data-color="purple">
            <div className="pc-icon">🔥</div>
            <div className="pc-value">{toFa(streak)}</div>
            <div className="pc-label">استریک</div>
            <div className="pc-sub">روز پیوسته</div>
          </div>
        </div>

        <div className="chart-card">
          <div className="chart-card-head">
            <h3>📊 توزیع سطح عملکرد</h3>
            <span className="chart-total">{toFa(total)} آزمون</span>
          </div>
          <div className="bars">
            {counts.map((c) => {
              const h = c.count === 0 ? 0 : Math.max(8, (c.count / maxCount) * 100);
              return (
                <div key={c.key} className="bar-col">
                  <div className="bar-wrap">
                    <div className="bar" style={{ height: `${h}%`, background: c.color }}>
                      {c.count > 0 && <span className="bar-val">{toFa(c.count)}</span>}
                    </div>
                  </div>
                  <div className="bar-lbl">
                    <span className="bar-emoji">{c.emoji}</span>
                    {c.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="chart-card">
          <div className="chart-card-head">
            <h3>📚 عملکرد به تفکیک فصل</h3>
          </div>
          <div className="subject-rows">
            {chapterData.length > 0 ? (
              chapterData.map((s, idx) => (
                <div key={`${s.bookTitle}-${s.chapterName}-${idx}`} className="subject-row" data-subject={s.bookTitle}>
                  <div className="subject-row-icon">{getSubjectEmoji(s.bookTitle)}</div>
                  <div className="subject-row-info">
                    <div className="subject-row-name">
                      <span>
                        {s.bookTitle ? `${s.bookTitle} — ` : ''}
                        {s.chapterName}
                      </span>
                      <span>{toFa(s.count)} آزمون · {toFa(s.avgPct)}٪</span>
                    </div>
                    <div className="subject-row-bar">
                      <div className="subject-row-fill" style={{ width: `${s.avgPct}%` }} />
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-3)', fontSize: 13 }}>
                هنوز داده‌ای موجود نیست
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // --- Bottom Sheet ---
  const renderSheet = () => {
    if (!sheetExamId) return null;
    const found = findExamById(sheetExamId);
    if (!found) return null;
    const exam = found.exam;
    const color = getAccent(exam.subject);
    const glow = getGlow(exam.subject);

    let actionBtn;
    if (exam.status === 'completed') {
      actionBtn = (
        <button className="sheet-start-btn sb-result" onClick={() => { closeSheet(); openResult(exam.id); }}>
          🏆 مشاهده کارنامه
        </button>
      );
    } else if (exam.status === 'pending') {
      actionBtn = <button className="sheet-start-btn sb-disabled" disabled>⏳ در انتظار تصحیح</button>;
    } else if (exam.status === 'expired') {
      actionBtn = <button className="sheet-start-btn sb-disabled" disabled>🔒 آزمون بسته شده</button>;
    } else if (exam.status === 'upcoming') {
      actionBtn = <button className="sheet-start-btn sb-disabled" disabled>⏳ در انتظار انتشار</button>;
    } else if (exam.status === 'locked') {
      actionBtn = (
        <button className="sheet-start-btn sb-gold" onClick={() => { closeSheet(); openCoinModal(exam.id); }}>
          🪙 خرید {toFa(exam.requiredCoins || 0)} سکه
        </button>
      );
    } else {
      actionBtn = (
        <button className="sheet-start-btn" onClick={() => { closeSheet(); handleStartExam(exam); }}>
          🚀 شروع مأموریت
        </button>
      );
    }

    return (
      <div className="sheet-overlay open" onClick={(e) => { if (e.target.classList.contains('sheet-overlay')) closeSheet(); }}>
        <div className="sheet">
          <div className="sheet-handle" />
          <button className="sheet-close" onClick={closeSheet}>✕</button>
          <div
            className="sheet-header"
            style={{ '--sheet-accent': color, '--sheet-accent-glow': glow }}
          >
            <div className="sheet-icon" style={{ background: color }}>{exam.mascot}</div>
            <div className="sheet-header-info">
              <h2>{exam.title}</h2>
              <p>{exam.subject} · {found.chapter?.name || ''}</p>
            </div>
          </div>
          <div className="sheet-desc">{exam.description}</div>
          <div className="sheet-info-grid">
            <div className="sheet-info-item">
              <span className="sii-icon">📝</span>
              <div><div className="sii-label">سوال</div><div className="sii-value">{toFa(exam.questions)} سوال</div></div>
            </div>
            <div className="sheet-info-item">
              <span className="sii-icon">⏱</span>
              <div><div className="sii-label">مدت</div><div className="sii-value">{toFa(exam.duration)} دقیقه</div></div>
            </div>
            <div className="sheet-info-item">
              <span className="sii-icon">📖</span>
              <div><div className="sii-label">کتاب</div><div className="sii-value">{found.book?.title || '—'}</div></div>
            </div>
            {exam.requiredCoins > 0 && (
              <div className="sheet-info-item">
                <span className="sii-icon">🪙</span>
                <div><div className="sii-label">هزینه ورود</div><div className="sii-value">{toFa(exam.requiredCoins)} سکه</div></div>
              </div>
            )}
          </div>
          {exam.hasReward && (
            <div className="sheet-reward">
              <span>🎁</span>
              <span>
                پاداش بر اساس نمره:{' '}
                {exam.coins > 0 && exam.xp > 0 && `تا ${toFa(exam.coins)} سکه و ${toFa(exam.xp)} XP`}
                {exam.coins > 0 && exam.xp === 0 && `تا ${toFa(exam.coins)} سکه`}
                {exam.coins === 0 && exam.xp > 0 && `تا ${toFa(exam.xp)} XP`}
              </span>
            </div>
          )}
          {actionBtn}
        </div>
      </div>
    );
  };

  // --- Result Modal ---
const renderResultModal = () => {
    if (!resultExamId) return null;
    const found = findExamById(resultExamId);
    if (!found) return null;
    const exam = found.exam;

    // ✅ تلاش در همه منابع ممکن — exam‌های عمومی فقط در getGlobalExams() هستند
    const candidates = [
      (exams || []).find((e) => idsEqual(e.id, resultExamId)),
      (typeof getGlobalExams === 'function' ? getGlobalExams() : []).find((e) => idsEqual(e.id, resultExamId)),
      exam._originalExam,
      exam,
    ].filter(Boolean);

    let originalExam = null;
    let result = null;
    for (const c of candidates) {
      const r = c.results?.find((x) => idsEqual(x.studentId, currentStudentId));
      if (r) { originalExam = c; result = r; break; }
    }

    // ✅ اگر باز هم پیدا نشد، یک پیام واضح نشان بده (نه null)
    if (!result) {
      return (
        <div className="modal-overlay open" onClick={(e) => { if (e.target.classList.contains('modal-overlay')) closeResult(); }}>
          <div className="modal" style={{ maxWidth: 440, textAlign: 'center' }}>
            <button className="modal-close" onClick={closeResult}>✕</button>
            <div style={{ fontSize: 48, marginBottom: 12 }}>⚠️</div>
            <h3 style={{ fontSize: 20, fontWeight: 900, marginBottom: 10 }}>کارنامه در دسترس نیست</h3>
            <p style={{ fontSize: 14, color: 'var(--text-2)', lineHeight: 1.9, marginBottom: 20 }}>
              نتیجه‌ی این آزمون در داده‌ها پیدا نشد. لطفاً صفحه را رفرش کنید.
            </p>
            <div className="result-actions">
              <button className="ra-primary" onClick={closeResult}>✕ بستن</button>
            </div>
          </div>
        </div>
      );
    }

    const questions = Array.isArray(originalExam.questions) ? originalExam.questions : [];
    
    const maxScore = questions.reduce((s, q) => s + toNumber(q.score, 1), 0) || exam.maxScore || 0;
    const userScore = toNumber(result.score, 0);
    const pct = maxScore > 0 ? Math.round((userScore / maxScore) * 100) : 0;
    const isPassed = userScore >= calculatePassThreshold(maxScore);
    const emoji = pct >= 90 ? '🌟' : pct >= 75 ? '🎉' : pct >= 50 ? '👍' : '📖';

    return (
      <div className="modal-overlay open" onClick={(e) => { if (e.target.classList.contains('modal-overlay')) closeResult(); }}>
        <div className="modal">
          <button className="modal-close" onClick={closeResult}>✕</button>
          <div className="result-hero">
            <div className="result-emoji">{emoji}</div>
            <div className="result-title">کارنامه آزمون</div>
            <div className="result-exam-name">{originalExam.title || exam.title}</div>
          </div>
          <div className="result-score-card">
            <div className={`result-score-circle ${isPassed ? '' : 'is-fail'}`}>
              <div className="rs-num">{toFa(userScore)}</div>
              <div className="rs-max">از {toFa(maxScore)}</div>
            </div>
            <div className="result-score-info">
              <div className="rsi-label">وضعیت</div>
              <div className="rsi-value">{toFa(pct)}٪</div>
              <div className={`rsi-status ${isPassed ? 'pass' : 'fail'}`}>
                {isPassed ? '✅ قبول' : '❌ مردود'}
              </div>
            </div>
          </div>
          <div className="result-questions">
            {questions.map((q, idx) => {
              const isEssay = q.type === 'essay' || q.type === 'file';
              const answer = result.answers?.find((a) => a.questionIndex === idx || a.questionId === q.id);
              const detail = result.details?.find((d) => d.questionIndex === idx);
              const isCorrect = detail?.isCorrect === true || answer?.isCorrect === true;
              const isWrong = detail?.isCorrect === false || (answer && !answer.isCorrect);
              const isPendingQ = isEssay && detail?.isCorrect == null;
              return (
                <div key={idx} className="result-q-row">
                  <span className="result-q-num">{toFa(idx + 1)}</span>
                  <span className="result-q-type">{isEssay ? '📝' : '🎯'}</span>
                  <span className={`result-q-status ${isCorrect ? 'correct' : isWrong ? 'wrong' : 'pending'}`}>
                    {isCorrect ? '✅ صحیح' : isWrong ? '❌ غلط' : '⏳'}
                  </span>
                  <span className="result-q-score">
                    {isPendingQ ? '…' : toFa(detail?.score ?? (isCorrect ? toNumber(q.score, 1) : 0))}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="result-actions">
            <button className="ra-primary" onClick={closeResult}>✕ بستن</button>
            <button className="ra-outline" onClick={() => window.print()}>🖨️ چاپ</button>
          </div>
        </div>
      </div>
    );
  };

  // --- Coin Modal ---
  const renderCoinModal = () => {
    if (!coinModalExamId) return null;
    const found = findExamById(coinModalExamId);
    if (!found) return null;
    const exam = found.exam;
    const required = exam.requiredCoins || exam._originalExam?.requiredCoins || 100;
    const shortage = Math.max(0, required - coins);
    return (
      <div className="modal-overlay open" onClick={(e) => { if (e.target.classList.contains('modal-overlay')) closeCoinModal(); }}>
        <div className="modal" style={{ maxWidth: 440, textAlign: 'center' }}>
          <div className="coin-modal-icon">🪙</div>
          <h3 style={{ fontSize: 20, fontWeight: 900, marginBottom: 10 }}>سکه کافی نیست</h3>
          <p style={{ fontSize: 14, color: 'var(--text-2)', lineHeight: 1.9, marginBottom: 20 }}>
            این آزمون <strong style={{ color: 'var(--amber-bright)' }}>{toFa(required)} سکه</strong> هزینه دارد.<br />
            موجودی شما: <strong>{toFa(coins)} سکه</strong><br />
            کمبود: <strong style={{ color: 'var(--rose)' }}>{toFa(shortage)} سکه</strong>
          </p>
          <div className="result-actions">
            <button className="ra-primary" onClick={() => navigate('/coins')}>🪙 خرید سکه</button>
            <button className="ra-outline" onClick={closeCoinModal}>✕ بستن</button>
          </div>
        </div>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════
  //  MAIN RENDER
  // ═══════════════════════════════════════════════════════════════
  return (
    <div className={`exams-student-app${isDark ? ' dark-mode' : ''}`}>
      <div className="bg-layer" />
      <div className="bg-orbs">
        <div className="orb" /><div className="orb" /><div className="orb" /><div className="orb" />
      </div>
      <div className="bg-grid" />

      <div className="app">
        <div className="top-band-wrap">
          {renderTopbar()}
          {renderGreeting()}
        </div>
        <div className="sticky-head">
          {renderTabsNav()}
        </div>

        <main>
          {activeTab === 'public' && (
            publicBookId === null
              ? renderBookList(publicBooks, 'public')
              : renderBookDetail(publicBooks.find((b) => b.id === publicBookId), 'public')
          )}
          {activeTab === 'group' && (
            groupBookId === null
              ? renderBookList(groupBooks, 'group')
              : renderBookDetail(groupBooks.find((b) => b.id === groupBookId), 'group')
          )}
          {activeTab === 'history' && renderHistoryTab()}
          {activeTab === 'performance' && renderPerformanceTab()}
        </main>
      </div>

      {renderSheet()}
      {renderResultModal()}
      {renderCoinModal()}
    </div>
  );
};

// محاسبه استریک (خارج از کامپوننت برای دسترسی از performance)
function computeStreak(userId, exams) {
  if (!userId) return 0;
  const days = new Set();
  exams.forEach((e) => (e.results || []).forEach((r) => {
    if (!idsEqual(r.studentId, userId)) return;
    if (!r.isFinalSubmit || !r.submittedAt) return;
    days.add(moment(r.submittedAt).format('YYYY-MM-DD'));
  }));
  if (days.size === 0) return 0;
  let streak = 0;
  let cursor = moment();
  if (!days.has(cursor.format('YYYY-MM-DD'))) {
    cursor = cursor.subtract(1, 'day');
    if (!days.has(cursor.format('YYYY-MM-DD'))) return 0;
  }
  while (days.has(cursor.format('YYYY-MM-DD'))) { streak++; cursor = cursor.subtract(1, 'day'); }
  return streak;
}

export default ExamsStudent;